import React, {useRef, useState} from 'react'
import {getInitials, avatarColorIndex} from '../lib/avatarInitials'
import {useClickAway} from '../hooks/useClickAway'
import styles from './AccountMenu.module.scss'

export type AccountMenuProps = {
  /** User's given name — drives the avatar initials and the menu heading. */
  firstname: string
  /** User's family name — second avatar initial. */
  lastname: string
  onLogout: () => void
  isLoggingOut: boolean
  /** Shown inside the panel when the logout call failed and the session is still open. */
  logoutError?: string | null
}

// Six avatar tints (see AccountMenu.module.scss); the tint index is derived
// from the initials so a given person always lands on the same colour. The
// initials + index logic lives in lib/avatarInitials; the admin SPA keeps its
// own copy of the same rule (no shared front-end package). Re-exported here for
// AccountMenu tests.
export {getInitials, avatarColorIndex}

/**
 * Account avatar for the top bar. The trigger is a
 * circle of 1–2 initials whose colour is derived from those initials; the full
 * name and "Salir" live inside the dropdown. The old global role tag was
 * removed — role is per store now and shown in the store switcher.
 */
export const AccountMenu = ({
  firstname,
  lastname,
  onLogout,
  isLoggingOut,
  logoutError = null,
}: AccountMenuProps): React.ReactElement => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickAway(ref, () => setOpen(false), open)

  const fullName = `${firstname} ${lastname}`.trim() || 'Cuenta'
  const initials = getInitials(firstname, lastname)
  const colorClass = styles[`account-menu__avatar--c${avatarColorIndex(initials)}`]

  return (
    <div className="modul" ref={ref}>
      <button
        type="button"
        className={['row-flx', 'flx-a-c', 'flx-j-c', styles['account-menu__trigger'], colorClass].filter(Boolean).join(' ')}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Cuenta de ${fullName}`}
      >
        <span className={`txt-white ${styles['account-menu__initials']}`} aria-hidden="true">
          {initials}
        </span>
      </button>

      {open && (
        <div className={`border-s bg-white ${styles['account-menu__panel']}`} role="menu">
          <div className={`col-flx m-b-4 ${styles['account-menu__panel-head']}`}>
            <span className={`txt-upper txt-light-kale ${styles['account-menu__panel-eyebrow']}`}>Sesión iniciada como</span>
            <span className={`txt-kale ${styles['account-menu__panel-name']}`}>{fullName}</span>
          </div>
          <button
            type="button"
            className={`txt-a-l border-xs full-width txt-bold-500 txt-kale ${styles['account-menu__logout']}`}
            onClick={onLogout}
            disabled={isLoggingOut}
            role="menuitem"
          >
            Salir
          </button>
          {logoutError !== null && (
            <p className={`m-t-4 m-h-0 m-b-0 border-xs bg-pale-grana txt-grana ${styles['account-menu__error']}`} role="alert">
              {logoutError}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
